import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Download, Heart, Pencil, Trash2 } from 'lucide-react';
import { GeneratedImage } from '../../services/api/imageGenerationService';
import { useCreativeAnalysis } from '../../hooks/useCreativeAnalysis';
import { CreativeIntentSection, CreativeAnalysisLoadingIndicator } from './CreativeIntentSection';

/**
 * カルーセル制作ページの詳細ビューア。
 *
 * バナー広告（BannerAdsPage の Image Detail Modal + BannerDetailPanel）と同じ
 * 「左に画像＋端の前後矢印／右に説明パネル」の2カラムレイアウトを踏襲する。
 * 履歴ギャラリーの「現在のビュー（自分／組織／お気に入り）」内の画像配列を受け取り、
 * 画像左右端の矢印もしくはキーボードの矢印キー、右パネルのサムネ一覧から、
 * セット内のスライドを前後・任意位置へ送れる。先頭・末尾では端の矢印を無効化し、
 * wrap させずにそこで止まる。
 *
 * 制作意図セクションはバナー詳細（BannerDetailPanel）と共通の CreativeIntentSection /
 * useCreativeAnalysis を使う（カルーセルのスライドも generated_images 基盤のため、
 * バナーと同じ creative-analysis API がそのまま動く）。
 */

interface CarouselImageViewerModalProps {
  images: GeneratedImage[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
  onToggleFavorite?: (img: GeneratedImage) => void;
  onDownload?: (img: GeneratedImage) => void;
  onImageEdit?: () => void;
  onDelete?: (img: GeneratedImage) => void;
}

const CarouselImageViewerModal: React.FC<CarouselImageViewerModalProps> = ({
  images,
  index,
  onClose,
  onIndexChange,
  onToggleFavorite,
  onDownload,
  onImageEdit,
  onDelete,
}) => {
  const total = images.length;
  const hasPrev = index > 0;
  const hasNext = index < total - 1;
  const current = images[index];
  // current は末尾スライド削除直後など一時的に undefined になりうる。
  // useCreativeAnalysis は null/undefined を許容するため、Rules of Hooks を守ったまま渡せる。
  const { analysis, isLoading, analysisError } = useCreativeAnalysis(current);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        if (index > 0) onIndexChange(index - 1);
      } else if (e.key === 'ArrowRight') {
        if (index < total - 1) onIndexChange(index + 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [index, total, onClose, onIndexChange]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col lg:flex-row min-h-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── 左: 画像 ── */}
        <div className="relative flex-1 flex items-center justify-center bg-black min-h-0">
          {onToggleFavorite && (
            <button
              type="button"
              onClick={() => onToggleFavorite(current)}
              className={`absolute top-4 left-4 h-10 w-10 rounded-md shadow-md flex items-center justify-center z-10 transition-colors ${
                current.is_favorited
                  ? 'bg-rose-500 text-white hover:bg-rose-600'
                  : 'bg-white hover:bg-gray-100 text-gray-700'
              }`}
              title={current.is_favorited ? 'お気に入りを解除' : 'お気に入りに追加'}
            >
              <Heart className={`w-5 h-5 ${current.is_favorited ? 'fill-current' : ''}`} />
            </button>
          )}

          <img
            src={current.url}
            alt={current.prompt}
            className="max-w-full max-h-full object-contain"
          />

          {/* 前の画像 */}
          <button
            type="button"
            onClick={() => hasPrev && onIndexChange(index - 1)}
            disabled={!hasPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white bg-opacity-90 text-gray-800 shadow-lg flex items-center justify-center hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all z-10"
            aria-label="前の画像"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          {/* 次の画像 */}
          <button
            type="button"
            onClick={() => hasNext && onIndexChange(index + 1)}
            disabled={!hasNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white bg-opacity-90 text-gray-800 shadow-lg flex items-center justify-center hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all z-10"
            aria-label="次の画像"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>

        {/* ── 右: 説明パネル ── */}
        <div className="w-full lg:w-96 flex flex-col bg-white min-h-0">
          {/* ヘッダ */}
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">カルーセル詳細</h3>
              <button
                type="button"
                onClick={onClose}
                className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded-md transition-colors"
                title="閉じる"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              {index + 1} / {images.length} 枚目
            </p>
          </div>

          {/* スクロール領域 */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* このページの役割 */}
            {current.metadata?.slide_role && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1">このページの役割</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{current.metadata.slide_role}</p>
              </div>
            )}

            {/* プロンプト / 商品 */}
            {current.input_mode === 'freetext' && current.prompt && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1">プロンプト</h4>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {current.prompt}
                </p>
              </div>
            )}
            {current.product_name && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1">商品</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{current.product_name}</p>
              </div>
            )}

            {/* 制作意図（バナー詳細と共通の表示部。分析未実行/取得中は小さいローディング表示、
                失敗時はセクション非表示で壊れないようにする） */}
            {isLoading ? (
              <CreativeAnalysisLoadingIndicator label="AIが制作意図を分析中です..." />
            ) : !analysisError ? (
              <CreativeIntentSection analysis={analysis} styleName={current.style_name} />
            ) : null}

            {/* スライド一覧 */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">スライド一覧</h4>
              <div className="grid grid-cols-4 gap-2">
                {images.map((img, i) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => onIndexChange(i)}
                    className="relative block"
                    aria-label={`${i + 1}枚目を表示`}
                  >
                    <img
                      src={img.url}
                      alt={`${i + 1}枚目`}
                      className={`w-full aspect-square object-cover rounded ${
                        i === index ? 'ring-2 ring-indigo-500' : ''
                      }`}
                    />
                    <span className="absolute top-0.5 left-0.5 bg-black bg-opacity-60 text-white text-[10px] leading-none px-1 py-0.5 rounded">
                      {i + 1}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* アクション（バナー詳細と同じ「画像を編集 / ダウンロード / 削除」の並び。
                お気に入りは画像左上のハートアイコンで操作する） */}
            <div className="space-y-2 pt-2">
              {onImageEdit && (
                <button
                  type="button"
                  onClick={onImageEdit}
                  className="w-full flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 px-3 rounded-md transition-colors text-sm font-medium"
                >
                  <Pencil className="w-4 h-4" />
                  画像を編集
                </button>
              )}
              <div className="grid grid-cols-2 gap-2">
                {onDownload && (
                  <button
                    type="button"
                    onClick={() => onDownload(current)}
                    className="flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 px-3 rounded-md transition-colors text-xs font-medium"
                  >
                    <Download className="w-3.5 h-3.5" />
                    ダウンロード
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(current)}
                    className="flex items-center justify-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 py-2 px-3 rounded-md transition-colors text-xs font-medium"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    削除
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400">
                {new Date(current.created_at).toLocaleDateString('ja-JP')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarouselImageViewerModal;
